import { useState, useEffect, useCallback } from 'react';
import type { Testimonial } from '../../types';

interface TestimonialCarouselProps {
  testimonials: Testimonial[];
  autoRotateMs?: number;
  showArrows?: boolean;
}

export default function TestimonialCarousel({
  testimonials,
  autoRotateMs = 5000,
  showArrows = false,
}: TestimonialCarouselProps) {
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (autoRotateMs <= 0) return;

    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % testimonials.length);
    }, autoRotateMs);

    return () => clearInterval(interval);
  }, [autoRotateMs, testimonials.length]);

  const goToPrev = useCallback(() => {
    setActiveIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  }, [testimonials.length]);

  const goToNext = useCallback(() => {
    setActiveIndex((prev) => (prev + 1) % testimonials.length);
  }, [testimonials.length]);

  const currentTestimonial = testimonials[activeIndex];

  return (
    <div className="testimonial-carousel">
      <div className="testimonial-carousel__body">
        {showArrows && testimonials.length > 1 && (
          <button
            type="button"
            className="testimonial-carousel__arrow testimonial-carousel__arrow--prev"
            onClick={goToPrev}
            aria-label="Previous testimonial"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
        )}

        <div className="testimonial-carousel__content" key={activeIndex}>
          <div className="testimonial-carousel__stars">
            {'★'.repeat(currentTestimonial.stars)}
          </div>
          <blockquote className="testimonial-carousel__quote">
            "{currentTestimonial.quote}"
          </blockquote>
          <div className="testimonial-carousel__author">
            {currentTestimonial.author && <span className="testimonial-carousel__name">{currentTestimonial.author}</span>}
            <span className="testimonial-carousel__title">{currentTestimonial.title}</span>
          </div>
        </div>

        {showArrows && testimonials.length > 1 && (
          <button
            type="button"
            className="testimonial-carousel__arrow testimonial-carousel__arrow--next"
            onClick={goToNext}
            aria-label="Next testimonial"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        )}
      </div>

      {testimonials.length > 1 && (
        <div className="testimonial-carousel__dots">
          {testimonials.map((_, index) => (
            <button
              key={index}
              type="button"
              className={`testimonial-carousel__dot ${index === activeIndex ? 'testimonial-carousel__dot--active' : ''}`}
              onClick={() => setActiveIndex(index)}
              aria-label={`View testimonial ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
